type Algorithm = 'sha256' | 'sha512'

/**
 * HMAC-SHA署名を生成する
 * @param params - 署名生成に必要なパラメータ
 * @param params.algorithm - 署名アルゴリズム（'sha256' または 'sha512'）
 * @param params.payload - 署名対象の文字列
 * @param params.secret - シークレットキー
 * @param params.encoding - 署名のエンコーディング形式（'hex' または 'base64'）
 * @returns 署名文字列
 */
export const sign = async (params: {
  algorithm: Algorithm
  payload: string
  secret: string
  encoding: 'hex' | 'base64'
}): Promise<string> => {
  const { algorithm, payload, secret, encoding } = params

  const enc = new TextEncoder()
  const algorithmName = 'HMAC'
  const algorithmHash = getHashType(algorithm)
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: algorithmName, hash: algorithmHash },
    false,
    ['sign', 'verify'],
  )
  const signature = await crypto.subtle.sign(
    algorithmName,
    key,
    enc.encode(payload),
  )

  return encode({
    data: signature,
    encoding,
  })
}

const getHashType = (algorithm: Algorithm): string => {
  switch (algorithm) {
    case 'sha256':
      return 'SHA-256'
    case 'sha512':
      return 'SHA-512'
    default:
      throw new Error(`Unsupported algorithm: ${algorithm satisfies never}`)
  }
}

const encode = (params: {
  data: ArrayBuffer
  encoding: 'hex' | 'base64'
}): string => {
  const { data, encoding } = params

  switch (encoding) {
    case 'hex': {
      return buf2hex(data)
    }
    case 'base64': {
      return btoa(String.fromCodePoint(...new Uint8Array(data)))
    }
    default: {
      throw new Error(`Unsupported encoding: ${encoding satisfies never}`)
    }
  }
}

const buf2hex = (buffer: ArrayBuffer) =>
  [...new Uint8Array(buffer)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')
